import React, { useCallback, useEffect, useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalHeader, ModalOverlay } from '@chakra-ui/modal';
import useEventBus from 'hooks/useEventBus';
import { Box, Flex, Heading } from '@chakra-ui/layout';
import styled from 'styled-components';
import { Button, CustomIcon } from 'components';
import { openNotification } from 'utils/notification';

const CModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
`;

interface IOption {
  title: string;
  text: string;
  img: string;
  href?: string;
  event?: string;
  disabled?: boolean;
  isPage: boolean;
}

const Popup: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [options, setOptions] = useState<IOption[]>([]);
  const eventBus = useEventBus();
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTitle('');
    setText('');
    setOptions([]);
  }, []);
  useEffect(() => {
    const onPopup = ({
      title,
      text,
      options,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isPage,
    }: {
      title?: string;
      text?: string;
      options?: IOption[];
      isPage: boolean;
    }) => {
      setTitle(title || '');
      setText(text || '');
      setIsOpen(true);
      setOptions(options || []);
    };
    eventBus.addEventListener('onPopup', onPopup);
    return () => {
      eventBus.removeEventListener('onPopup', onPopup);
    };
  }, []);
  return (
    <CModal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent
        w="auto"
        maxW={options.length > 0 ? 'full' : undefined}
        p="24px"
        height="auto"
        alignSelf="center"
      >
        <ModalHeader
          display="flex"
          p="0"
          mb="1rem"
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading fontSize="18px" fontWeight="700" color="brand.tertiary">
            {title}
          </Heading>
          <Box
            bg="brand.1300"
            borderRadius="50%"
            fontSize="14px"
            fontWeight="bold"
            display="flex"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            as="button"
            border="none"
            color="brand.red100"
            onClick={handleClose}
          >
            <CustomIcon size="22px" $noSphere icon="round_close" />
          </Box>
        </ModalHeader>
        <ModalBody w="auto" maxW="full" p="0">
          <Heading width="100%" fontSize="14px" fontWeight="400" color="brand.1400">
            {text}
          </Heading>
          {options.length > 0 && (
            <Flex overflow="auto" gridGap="16px" direction="row">
              {options.map((i, k) => (
                <Box
                  p="16px"
                  flexShrink={0}
                  border="1px solid"
                  borderColor="brand.gray100"
                  borderRadius="8px"
                  w={options.length >= 3 ? '250px' : '300px'}
                  h="350px"
                  as="a"
                  href={i.href}
                  onClick={(e: any) => {
                    if (i.disabled && i.isPage === false) {
                      e.preventDefault();
                      openNotification(
                        'Função indisponível. Estamos trabalhando para lançar esta função em breve!',
                        'info',
                      );
                    } else if (i.disabled && i.isPage) {
                      e.preventDefault();
                      openNotification('Você não possui acesso a esta função.', 'warn');
                    }
                    if (typeof i.href !== 'string' && i.event) eventBus.dispatchEvent(i.event);
                  }}
                  key={k}
                  transition="all 300ms cubic-bezier(.19,.98,1,.55)"
                  _hover={{
                    borderColor: 'brand.tertiary',
                    borderWidth: '2px',
                    boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.25)',
                  }}
                >
                  <Box /* w="141px" */ h="120px" mx="auto" mb="27px" as="img" src={i.img} />
                  <Heading as="h4" mb="8px" fontSize="16px" color="brand.tertiary" fontWeight="700">
                    {i.title}
                  </Heading>
                  <Heading
                    lineHeight="21px"
                    as="p"
                    fontSize="14px"
                    color="brand.gray400"
                    fontWeight="400"
                  >
                    {i.text}
                  </Heading>
                </Box>
              ))}
            </Flex>
          )}
          {options.length <= 0 && (
            <Box mt="16px" width="100%" display="flex" alignItems="center" justifyContent="center">
              <Button onClick={handleClose} type="button" outline variant="primary">
                Entendido
              </Button>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </CModal>
  );
};

export default Popup;
